import { useCallback } from "react";
import { ChevronDown, MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface TableAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: "default" | "destructive";
  disabled?: boolean;
}

export interface TableActionsMenuProps {
  actions: TableAction[];
  onTriggerClick?: (e: React.MouseEvent) => void;
  /** When set, show a label + chevron trigger instead of the icon-only button. */
  triggerLabel?: string;
  /** Disable the trigger button (e.g. while an action is in progress). */
  disabled?: boolean;
}

export function TableActionsMenu({
  actions,
  onTriggerClick,
  triggerLabel,
  disabled,
}: TableActionsMenuProps): JSX.Element {
  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onTriggerClick?.(e);
    },
    [onTriggerClick]
  );

  const handleActionClick = useCallback(
    (action: TableAction) =>
      (e: React.MouseEvent) => {
        e.stopPropagation();
        action.onClick();
      },
    []
  );

  if (actions.length === 0) {
    return <></>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={triggerLabel ? "outline" : "ghost"}
          size={triggerLabel ? "sm" : "icon"}
          onClick={handleTriggerClick}
          aria-label={triggerLabel ?? "Row actions"}
          className={triggerLabel ? "gap-1.5" : undefined}
          disabled={disabled}
        >
          {triggerLabel ? (
            <>
              {triggerLabel}
              <ChevronDown className="h-4 w-4" />
            </>
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <DropdownMenuItem
              key={index}
              disabled={action.disabled}
              onClick={handleActionClick(action)}
              className={
                action.variant === "destructive" ? "text-destructive" : undefined
              }
            >
              <Icon className="mr-2 h-4 w-4" />
              {action.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
